import React, { FC, useEffect, useRef, useCallback, KeyboardEventHandler, useState } from 'react';

// Hooks
import useScrollLock from '../../hooks/useAddFeatureFlagToRoot/useScrollLock';
import useFocusTrap from '../../hooks/useFocusTrap';
import useClickOutside from '../../hooks/useClickOutside';
import useForceUpdateOnWindowResize from '../../hooks/useForceUpdateOnWindowResize';

// Components
import { CloseIcon } from '../SvgIcons';
import ModalContainer from './ModalContainer';

import type { ModalProps } from './Modal';

import { Overlay, ModalDialog, CloseModalButton, ScreenReaderText } from './ModalV2.styles';

export const MODAL_ANIMATION_ENDED_EVENT = 'MODAL_ANIMATION_ENDED';

/**
 * ModalV2 is a "drop-in" replacement for Modal.  This modal addresses some deficiencies
 * with ModalV1.
 *
 * In particular, the ModalV1:
 *
 * - does not support mobile styles
 * - adds padding to the content
 * - positions the close button in a way that does not allow for content in the modal to push to the edge.
 * - does not follow the design system for scrolling content
 *
 * For the best use of ModalV2, you should use ModalContent for the children
 *
 * Design system reference: [Components/Modal](https://www.figma.com/file/kzG9IDk1npM79QRzaU9XUh/Zola-Design-System-Documentation?node-id=101%3A940)
 *
 * @example
  {modalOpen ? (
        <ModalV2
          rootId={modalRootId}
          triggerRef={triggerRef}
          dialogHeadingId="modal-header"
          onClose={() => {
            setModalOpen(false);
          }}
        >
          <ModalContent
            className="modal-content blue-background-modal"
            title={{
              id: 'modal-header',
              hed: 'Lorem Ipsum Header',
            }}
            controls={renderControls()}
            alignControlsLeft
          />
        </ModalV2>
      ) : null}
 */
const ModalV2: FC<ModalProps> = ({
  children,
  dialogHeadingId = 'dialog-heading',
  onClose,
  rootId,
  triggerRef,
  className,
  lockBgScrolling = false,
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
}) => {
  useForceUpdateOnWindowResize();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const handleTabbing = useFocusTrap(dialogRef);
  const { onDisableLock, onEnableLock } = useScrollLock(lockBgScrolling);

  useEffect(() => {
    onEnableLock();
    return () => {
      onDisableLock();
    };
  }, [onDisableLock, onEnableLock]);

  const onCloseCallback = useCallback(onClose, [onClose]);

  useClickOutside({
    ref: dialogRef,
    onClickOutside: onCloseCallback,
    isOpen: true && closeOnOverlayClick,
  });

  useEffect(() => {
    // Close modal when hitting "Esc".
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnEscape) {
        event.preventDefault();
        event.stopPropagation();
        onCloseCallback();
      }
    };
    window.addEventListener('keydown', close, true);
    const triggeringButton = triggerRef?.current;

    return function removeEventListenerAndReturnFocus() {
      window.removeEventListener('keydown', close, true);
      if (triggeringButton) {
        triggeringButton.focus();
      }
    };
  }, [closeOnEscape, onCloseCallback, triggerRef]);

  const handlePressEnter: KeyboardEventHandler<HTMLButtonElement> = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      onCloseCallback();
    }
  };

  const [isFullScreen, setIsFullScreen] = useState(false);
  const calculateTopPosition = () => {
    if (dialogRef.current) {
      const topPosition = dialogRef.current.offsetTop;
      if (topPosition === 0) {
        setIsFullScreen(true);
      } else {
        setIsFullScreen(false);
      }
    }
  };
  const onAnimationEnd = () => {
    calculateTopPosition();
    window.dispatchEvent(new CustomEvent(MODAL_ANIMATION_ENDED_EVENT));
  };

  useEffect(calculateTopPosition);

  return (
    <ModalContainer rootId={rootId}>
      <Overlay className={className} id="z-modal-overlay-v2">
        <ModalDialog
          role="dialog"
          aria-modal="true"
          aria-labelledby={dialogHeadingId}
          ref={dialogRef}
          onKeyDown={handleTabbing}
          squareCorners={isFullScreen}
          onAnimationEnd={onAnimationEnd}
        >
          {showCloseButton && (
            <CloseModalButton
              id="zCloseModal"
              onClick={onCloseCallback}
              onKeyDown={handlePressEnter}
              ref={closeButtonRef}
            >
              <CloseIcon showTitle={false} />
              <ScreenReaderText>Close</ScreenReaderText>
            </CloseModalButton>
          )}
          {children}
        </ModalDialog>
      </Overlay>
    </ModalContainer>
  );
};

export default ModalV2;
